/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Alert, 
  AlertTitle, 
  Button, 
  CircularProgress, 
  Collapse,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import { 
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

interface EmailVerificationBannerProps {
  variant?: 'banner' | 'card';
  showDetails?: boolean;
}

const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({ 
  variant = 'banner',
  showDetails = false 
}) => {
  const { t } = useTranslation();
  const { user, resendVerificationEmail } = useAuth();
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showLimitations, setShowLimitations] = useState(showDetails);

  // Don't show if user is verified or not logged in
  if (!user || user.isEmailVerified) {
    return null;
  }

  // Check if user is pending approval
  const isPendingApproval = (user as any).accountStatus === 'pending_approval';

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await resendVerificationEmail();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      logger.error('Error resending verification email:', error);
    } finally {
      setResending(false);
    }
  };

  const limitations = [
    { text: t('creatingNewDebates'), icon: <BlockIcon color="error" /> },
    { text: t('joiningDebatesViaInvites'), icon: <BlockIcon color="error" /> },
    { text: t('receivingEmailNotifications'), icon: <BlockIcon color="error" /> },
    { text: t('participatingInTeamDebates'), icon: <BlockIcon color="error" /> },
    { text: t('accessingPremiumFeatures'), icon: <BlockIcon color="error" /> }
  ];

  const available = [
    { text: t('viewingPublicDebates'), icon: <CheckCircleIcon color="success" /> },
    { text: t('browsingTheCube'), icon: <CheckCircleIcon color="success" /> },
    { text: t('viewingLeaderboards'), icon: <CheckCircleIcon color="success" /> },
    { text: t('readingRulesAndAbout'), icon: <CheckCircleIcon color="success" /> }
  ];

  if (variant === 'card') {
    return (
      <Alert 
        severity="warning" 
        sx={{ mb: 3 }}
        action={
          <Button 
            color="inherit" 
            size="small" 
            onClick={handleResendVerification}
            disabled={resending}
            startIcon={resending ? <CircularProgress size={16} /> : <EmailIcon />}
          >
            {resending ? t('sending') : t('resendEmail')}
          </Button>
        }
      >
        <AlertTitle>{t('emailVerificationRequired')}</AlertTitle>
        {t('pleaseVerifyEmail')}
        
        {resendSuccess && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            {t('verificationEmailSentSuccess')}
          </Typography>
        )}
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Alert 
        severity="warning" 
        action={
          <Box>
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setShowLimitations(!showLimitations)}
              sx={{ mr: 1 }}
            >
              {showLimitations ? t('hideDetails') : t('showDetails')}
            </Button>
            {!isPendingApproval && (
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleResendVerification}
              disabled={resending}
              startIcon={resending ? <CircularProgress size={16} /> : <EmailIcon />}
            >
              {resending ? t('sending') : t('resendEmail')}
            </Button>
            )}
          </Box>
        }
      >
        <AlertTitle>{isPendingApproval ? t('limitedAccessPendingApproval') : t('limitedAccessVerifyEmail')}</AlertTitle>
        {isPendingApproval ? (
          <>
            {t('nobleUserPendingApproval', { username: user.username }).split('{username}')[0]}
            <strong>{user.username}</strong>
            {t('nobleUserPendingApproval', { username: user.username }).split('{username}').slice(1).join('')}
          </>
        ) : (
          <>
            {t('nobleUserLimitedAccess', { username: user.username }).split('{username}')[0]}
            <strong>{user.username}</strong>
            {t('nobleUserLimitedAccess', { username: user.username }).split('{username}').slice(1).join('')}
          </>
        )}
        
        {resendSuccess && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            {t('verificationEmailSentSuccess')}
          </Typography>
        )}
      </Alert>

      <Collapse in={showLimitations}>
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'warning.main' }}>
          <Typography variant="h6" gutterBottom color="warning.main">
            {t('currentlyUnavailable')}
          </Typography>
          <List dense>
            {limitations.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom color="success.main" sx={{ mt: 2 }}>
            {t('availableNow')}
          </Typography>
          <List dense>
            {available.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>{t('tipCheckSpamFolder')}</strong>
          </Alert>
        </Box>
      </Collapse>
    </Box>
  );
};

export default EmailVerificationBanner; 